/** @jsxImportSource @emotion/react */
/* eslint-disable react/jsx-props-no-spreading */
import { forwardRef, useId } from "react";
import PropTypes from "prop-types";
import RSelect from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { AsyncPaginate, withAsyncPaginate } from "react-select-async-paginate";
import { errorCss, wrapperCss } from "assets/css/fieldset.css";
import useMenuPortalTargetRef from "hooks/useMenuPortalTargetRef";
import useComponents from "./useComponents";
import useStyles from "./useStyles";
import Label from "./Label";

const CreatableAsyncPaginate = withAsyncPaginate(CreatableSelect);

const SELECT_COMPONENT = {
  async: AsyncPaginate,
  asyncCreatable: CreatableAsyncPaginate,
  creatable: CreatableSelect,
  asyncSelect: AsyncSelect,
};

const Select = forwardRef(
  ({ label, popperText, error, selectComponent, className, styles, components, ...props }, ref) => {
    const { current: menuPortalTarget } = useMenuPortalTargetRef();
    const inputId = useId();
    const mergedStyles = useStyles(styles);
    const mergedComponents = useComponents(components);
    const SelectComponent = SELECT_COMPONENT[selectComponent] ?? RSelect;

    return (
      <div css={wrapperCss} className={className}>
        <Label label={label} inputId={inputId} popperText={popperText} />
        <SelectComponent
          ref={ref}
          inputId={inputId}
          components={mergedComponents}
          styles={mergedStyles}
          menuPortalTarget={menuPortalTarget}
          {...props}
        />
        {!!error && <span css={errorCss}>{error}</span>}
      </div>
    );
  },
);

export default Select;

Select.propTypes = {
  isMulti: PropTypes.bool,
  variant: PropTypes.oneOf(["outlined", "filled"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  styles: PropTypes.shape({}),
  components: PropTypes.shape({}),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  defaultValue: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ),
  ]),
  isGrey: PropTypes.bool,
  hasError: PropTypes.bool,
  valid: PropTypes.bool,
  error: PropTypes.string,
  isClearable: PropTypes.bool,
  name: PropTypes.string,
  popperText: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  label: PropTypes.string,
  selectComponent: PropTypes.oneOf(["async", "creatable", "asyncCreatable", "asyncSelect"]),
  loadOptions: PropTypes.func,
  additional: PropTypes.shape({
    page: PropTypes.number,
  }),
  defaultOptions: PropTypes.bool,
  debounceTimeout: PropTypes.number,
  hideSelectedOptions: PropTypes.bool,
};
